/**
 * useAufgabenExtras — Hook für erweiterte Aufgaben-Features
 * 
 * Features:
 * - Datei-Upload (Supabase Storage Bucket: aufgaben-dateien)
 * - Kommentare (CRUD)
 * - Labels/Tags (CRUD + Zuordnung)
 * - Unteraufgaben (CRUD)
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { MAX_FILE_SIZE } from '@/lib/aufgaben-utils';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AufgabeDatei {
  id: string;
  aufgabe_id: string;
  dateiname: string;
  dateipfad: string;
  dateigroesse: number;
  dateityp: string;
  hochgeladen_von: string | null;
  created_at: string;
  /**
   * Temporäre Signed URL aus dem privaten `aufgaben-dateien` Bucket.
   * Wird beim Laden in `enrichAufgabenDateienMitSignedUrls` befüllt; für
   * base64 Inline-Dateien bleibt das Feld `undefined` und Komponenten
   * fallen auf `dateipfad` zurück.
   */
  signedUrl?: string;
}

const AUFGABEN_DATEIEN_BUCKET = 'aufgaben-dateien';
const AUFGABEN_DATEIEN_SIGNED_URL_TTL_SECONDS = 3600;

interface AufgabenDateienStorage {
  createSignedUrl: (
    path: string,
    expiresIn: number,
  ) => Promise<{ data: { signedUrl: string } | null; error: unknown }>;
}

/**
 * Extrahiert den Storage-Pfad aus einem `dateipfad`-Wert. Akzeptiert:
 *   - reine Storage-Pfade (`<aufgabeId>/<ts>_<name>`) → unverändert
 *   - alte Public-URLs (`.../object/public/aufgaben-dateien/<path>`)
 *   - Signed URLs (`.../object/sign/aufgaben-dateien/<path>?token=...`)
 * Gibt `null` zurück für base64 Data-URLs oder URLs ohne Bucket-Segment.
 */
export function extractAufgabeDateiStoragePath(
  dateipfadOrUrl: string,
): string | null {
  if (!dateipfadOrUrl || dateipfadOrUrl.startsWith('data:')) return null;
  if (!dateipfadOrUrl.startsWith('http')) return dateipfadOrUrl;
  const marker = `/${AUFGABEN_DATEIEN_BUCKET}/`;
  const idx = dateipfadOrUrl.indexOf(marker);
  if (idx === -1) return null;
  return dateipfadOrUrl.slice(idx + marker.length).split('?')[0];
}

/**
 * Hängt eine zeitlich begrenzte Signed URL an jede Datei mit Storage-Pfad.
 * Base64-Inline-Dateien bleiben unverändert. Fehlschläge einer einzelnen
 * Datei beeinflussen die anderen nicht (kein Throw, nur fehlendes Feld).
 */
export async function enrichAufgabenDateienMitSignedUrls(
  rows: AufgabeDatei[],
  storage: AufgabenDateienStorage,
): Promise<AufgabeDatei[]> {
  return Promise.all(
    rows.map(async (row) => {
      const path = extractAufgabeDateiStoragePath(row.dateipfad);
      if (!path) return row;
      const { data, error } = await storage.createSignedUrl(
        path,
        AUFGABEN_DATEIEN_SIGNED_URL_TTL_SECONDS,
      );
      if (error || !data?.signedUrl) return row;
      return { ...row, signedUrl: data.signedUrl };
    }),
  );
}

export interface AufgabeKommentar {
  id: string;
  aufgabe_id: string;
  autor_id: string;
  inhalt: string;
  created_at: string;
  updated_at: string;
  // Client-seitig gejoint
  autor?: { vorname: string; nachname: string };
}

export interface AufgabeLabel {
  id: string;
  name: string;
  farbe: string;
  erstellt_von: string | null;
  created_at: string;
}

export interface Unteraufgabe {
  id: string;
  aufgabe_id: string;
  titel: string;
  erledigt: boolean;
  sortierung: number;
  erstellt_von: string | null;
  created_at: string;
}

// ─── DATEIEN ────────────────────────────────────────────────────────────────

export function useAufgabenDateien(aufgabeId: string | null) {
  const [dateien, setDateien] = useState<AufgabeDatei[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchDateien = useCallback(async () => {
    if (!aufgabeId) { setDateien([]); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('aufgaben_dateien')
        .select('*')
        .eq('aufgabe_id', aufgabeId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const enriched = await enrichAufgabenDateienMitSignedUrls(
        (data || []) as AufgabeDatei[],
        supabase.storage.from(AUFGABEN_DATEIEN_BUCKET),
      );
      setDateien(enriched);
    } catch (err: any) {
      console.error('Dateien laden fehlgeschlagen:', err);
      setDateien([]);
    } finally {
      setLoading(false);
    }
  }, [aufgabeId]);

  useEffect(() => { fetchDateien(); }, [fetchDateien]);

  const uploadDatei = useCallback(async (file: File, userId: string): Promise<AufgabeDatei | null> => {
    if (!aufgabeId) return null;
    try {
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${aufgabeId}/${timestamp}_${safeName}`;

      // Upload zu Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(AUFGABEN_DATEIEN_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Storage Upload Fehler:', uploadError);
        // Fallback: Base64 für Dateien bis MAX_FILE_SIZE (50 MB)
        if (file.size <= MAX_FILE_SIZE) {
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(file);
          });
          
          const { data: dbData, error: dbError } = await supabase
            .from('aufgaben_dateien')
            .insert({
              aufgabe_id: aufgabeId,
              dateiname: file.name,
              dateipfad: base64,
              dateigroesse: file.size,
              dateityp: file.type,
              hochgeladen_von: userId,
            })
            .select()
            .single();

          if (dbError) throw dbError;
          toast.success('Datei hochgeladen', { description: file.name });
          await fetchDateien();
          return dbData as AufgabeDatei;
        }
        throw uploadError;
      }

      // Storage-Pfad in der DB speichern (nicht die Public-URL — der Bucket
      // ist privat, der Public-URL wäre nicht abrufbar). Beim Lesen erzeugt
      // `enrichAufgabenDateienMitSignedUrls` zur Laufzeit eine Signed URL.
      const { data: dbData, error: dbError } = await supabase
        .from('aufgaben_dateien')
        .insert({
          aufgabe_id: aufgabeId,
          dateiname: file.name,
          dateipfad: uploadData.path,
          dateigroesse: file.size,
          dateityp: file.type,
          hochgeladen_von: userId,
        })
        .select()
        .single();

      if (dbError) throw dbError;
      toast.success('Datei hochgeladen', { description: file.name });
      await fetchDateien();
      return dbData as AufgabeDatei;
    } catch (err: any) {
      console.error('Upload fehlgeschlagen:', err);
      toast.error('Upload fehlgeschlagen', { description: err.message });
      return null;
    }
  }, [aufgabeId, fetchDateien]);

  const deleteDatei = useCallback(async (dateiId: string, dateipfad: string): Promise<boolean> => {
    try {
      // Aus Storage löschen (nur wenn kein Base64). Funktioniert sowohl für
      // neue Pfade als auch für alte Public/Signed URLs.
      const storagePath = extractAufgabeDateiStoragePath(dateipfad);
      if (storagePath) {
        await supabase.storage.from(AUFGABEN_DATEIEN_BUCKET).remove([storagePath]);
      }
      // Aus DB löschen
      const { error } = await supabase
        .from('aufgaben_dateien')
        .delete()
        .eq('id', dateiId);
      if (error) throw error;
      toast.success('Datei gelöscht');
      await fetchDateien();
      return true;
    } catch (err: any) {
      console.error('Löschen fehlgeschlagen:', err);
      toast.error('Löschen fehlgeschlagen');
      return false;
    }
  }, [fetchDateien]);

  return { dateien, loading, uploadDatei, deleteDatei, refetch: fetchDateien };
}

// ─── KOMMENTARE ─────────────────────────────────────────────────────────────

export function useAufgabenKommentare(aufgabeId: string | null) {
  const [kommentare, setKommentare] = useState<AufgabeKommentar[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchKommentare = useCallback(async () => {
    if (!aufgabeId) { setKommentare([]); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('aufgaben_kommentare')
        .select('*')
        .eq('aufgabe_id', aufgabeId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      // Mitarbeiter-Daten für Autoren laden
      const autorIds = Array.from(new Set((data || []).map(k => k.autor_id)));
      let autorMap: Record<string, { vorname: string; nachname: string }> = {};
      if (autorIds.length > 0) {
        const { data: mitarbeiter } = await supabase
          .from('mitarbeiter')
          .select('id, vorname, nachname')
          .in('id', autorIds);
        if (mitarbeiter) {
          mitarbeiter.forEach(m => { autorMap[m.id] = { vorname: m.vorname, nachname: m.nachname }; });
        }
      }

      setKommentare((data || []).map(k => ({
        ...k,
        autor: autorMap[k.autor_id] || undefined,
      })));
    } catch (err: any) {
      console.error('Kommentare laden fehlgeschlagen:', err);
      setKommentare([]);
    } finally {
      setLoading(false);
    }
  }, [aufgabeId]);

  useEffect(() => { fetchKommentare(); }, [fetchKommentare]);

  const addKommentar = useCallback(async (inhalt: string, autorId: string): Promise<boolean> => {
    if (!aufgabeId || !inhalt.trim()) return false;
    try {
      const { error } = await supabase
        .from('aufgaben_kommentare')
        .insert({ aufgabe_id: aufgabeId, autor_id: autorId, inhalt: inhalt.trim() });
      if (error) throw error;
      await fetchKommentare();
      return true;
    } catch (err: any) {
      console.error('Kommentar fehlgeschlagen:', err);
      toast.error('Kommentar fehlgeschlagen');
      return false;
    }
  }, [aufgabeId, fetchKommentare]);

  const deleteKommentar = useCallback(async (kommentarId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('aufgaben_kommentare')
        .delete()
        .eq('id', kommentarId);
      if (error) throw error;
      await fetchKommentare();
      return true;
    } catch (err: any) {
      console.error('Löschen fehlgeschlagen:', err);
      return false;
    }
  }, [fetchKommentare]);

  return { kommentare, loading, addKommentar, deleteKommentar, refetch: fetchKommentare };
}

// ─── LABELS ─────────────────────────────────────────────────────────────────

export function useAufgabenLabels() {
  const [labels, setLabels] = useState<AufgabeLabel[]>([]);

  const fetchLabels = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('aufgaben_labels')
        .select('*')
        .order('name');
      if (error) throw error;
      setLabels(data || []);
    } catch {
      setLabels([]);
    }
  }, []);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  const createLabel = useCallback(async (name: string, farbe: string, userId: string): Promise<AufgabeLabel | null> => {
    try {
      const { data, error } = await supabase
        .from('aufgaben_labels')
        .insert({ name, farbe, erstellt_von: userId })
        .select()
        .single();
      if (error) throw error;
      await fetchLabels();
      return data as AufgabeLabel;
    } catch (err: any) {
      toast.error('Label erstellen fehlgeschlagen');
      return null;
    }
  }, [fetchLabels]);

  const deleteLabel = useCallback(async (labelId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('aufgaben_labels').delete().eq('id', labelId);
      if (error) throw error;
      await fetchLabels();
      return true;
    } catch {
      return false;
    }
  }, [fetchLabels]);

  return { labels, createLabel, deleteLabel, refetch: fetchLabels };
}

export function useAufgabeLabelZuordnung(aufgabeId: string | null) {
  const [zugeordneteLabels, setZugeordneteLabels] = useState<string[]>([]);

  const fetchZuordnung = useCallback(async () => {
    if (!aufgabeId) { setZugeordneteLabels([]); return; }
    try {
      const { data, error } = await supabase
        .from('aufgaben_label_zuordnung')
        .select('label_id')
        .eq('aufgabe_id', aufgabeId);
      if (error) throw error;
      setZugeordneteLabels((data || []).map(d => d.label_id));
    } catch {
      setZugeordneteLabels([]);
    }
  }, [aufgabeId]);

  useEffect(() => { fetchZuordnung(); }, [fetchZuordnung]);

  const toggleLabel = useCallback(async (labelId: string): Promise<boolean> => {
    if (!aufgabeId) return false;
    try {
      if (zugeordneteLabels.includes(labelId)) {
        const { error } = await supabase
          .from('aufgaben_label_zuordnung')
          .delete()
          .eq('aufgabe_id', aufgabeId)
          .eq('label_id', labelId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('aufgaben_label_zuordnung')
          .insert({ aufgabe_id: aufgabeId, label_id: labelId });
        if (error) throw error;
      }
      await fetchZuordnung();
      return true;
    } catch {
      return false;
    }
  }, [aufgabeId, zugeordneteLabels, fetchZuordnung]);

  return { zugeordneteLabels, toggleLabel, refetch: fetchZuordnung };
}

// ─── UNTERAUFGABEN ──────────────────────────────────────────────────────────

export function useUnteraufgaben(aufgabeId: string | null) {
  const [unteraufgaben, setUnteraufgaben] = useState<Unteraufgabe[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchUnteraufgaben = useCallback(async () => {
    if (!aufgabeId) { setUnteraufgaben([]); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('unteraufgaben')
        .select('*')
        .eq('aufgabe_id', aufgabeId)
        .order('sortierung', { ascending: true });
      if (error) throw error;
      setUnteraufgaben(data || []);
    } catch {
      setUnteraufgaben([]);
    } finally {
      setLoading(false);
    }
  }, [aufgabeId]);

  useEffect(() => { fetchUnteraufgaben(); }, [fetchUnteraufgaben]);

  const addUnteraufgabe = useCallback(async (titel: string, userId: string): Promise<boolean> => {
    if (!aufgabeId || !titel.trim()) return false;
    try {
      const maxSort = unteraufgaben.length > 0 ? Math.max(...unteraufgaben.map(u => u.sortierung)) + 1 : 0;
      const { error } = await supabase
        .from('unteraufgaben')
        .insert({ aufgabe_id: aufgabeId, titel: titel.trim(), erstellt_von: userId, sortierung: maxSort });
      if (error) throw error;
      await fetchUnteraufgaben();
      return true;
    } catch {
      return false;
    }
  }, [aufgabeId, unteraufgaben, fetchUnteraufgaben]);

  const toggleUnteraufgabe = useCallback(async (id: string, erledigt: boolean): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('unteraufgaben')
        .update({ erledigt: !erledigt })
        .eq('id', id);
      if (error) throw error;
      await fetchUnteraufgaben();
      return true;
    } catch {
      return false;
    }
  }, [fetchUnteraufgaben]);

  const deleteUnteraufgabe = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('unteraufgaben').delete().eq('id', id);
      if (error) throw error;
      await fetchUnteraufgaben();
      return true;
    } catch {
      return false;
    }
  }, [fetchUnteraufgaben]);

  return { unteraufgaben, loading, addUnteraufgabe, toggleUnteraufgabe, deleteUnteraufgabe, refetch: fetchUnteraufgaben };
}
